import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Badge,
  Box,
  Button,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import React, { useState } from 'react';

interface FileUpload {
  file: File & { webkitRelativePath: string };
  status: 'pending' | 'processing' | 'success' | 'error';
  taskId?: string;
  error?: string;
}

interface UploadStatusDropdownProps {
  /** Array contenente lo stato corrente di tutti i file in coda di upload */
  uploads: FileUpload[];
}

/**
 * UploadStatusDropdown Component
 *
 * Un menu a comparsa ad allineamento geometrico a 40px che traccia lo stato
 * di avanzamento dei caricamenti massivi. Include un badge numerico reattivo.
 */
const UploadStatusDropdown = ({ uploads }: UploadStatusDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const activeUploadsCount = uploads.filter(
    (u) => u.status === 'pending' || u.status === 'processing'
  ).length;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Badge
        badgeContent={activeUploadsCount}
        color="warning"
        max={99}
        sx={{
          '& .MuiBadge-badge': {
            right: 4,
            top: 4,
            fontWeight: 700,
            fontSize: '0.7rem'
          }
        }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClick}
          startIcon={<CloudUploadIcon />}
          sx={{
            height: 40,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            whiteSpace: 'nowrap'
          }}>
          STATO UPLOAD
        </Button>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 320,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            mt: 1
          }
        }}>
        {uploads.length === 0 && (
          <MenuItem disabled sx={{ py: 2, justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Nessun upload in corso
            </Typography>
          </MenuItem>
        )}

        {uploads.map((u, idx) => {
          const filePath = u.file.webkitRelativePath || u.file.name;

          return (
            <Box key={`upload-item-${idx}`}>
              <MenuItem
                disableRipple
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 1.5,
                  px: 2,
                  gap: 1,
                  cursor: 'default',
                  '&:hover': { bgcolor: 'transparent' }
                }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {filePath}
                </Typography>

                {/* Stato: In Attesa */}
                {u.status === 'pending' && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      In attesa
                    </Typography>
                  </Box>
                )}

                {/* Stato: In Elaborazione Celery */}
                {u.status === 'processing' && (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress color="primary" sx={{ height: 6, borderRadius: 1, my: 1 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      Task: {u.taskId || 'Generazione in corso...'}
                    </Typography>
                  </Box>
                )}

                {/* Stato: Successo */}
                {u.status === 'success' && (
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'success.main' }}>
                    <CheckCircleOutlineIcon fontSize="inherit" />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Successo
                    </Typography>
                  </Box>
                )}

                {/* Stato: Errore API */}
                {u.status === 'error' && (
                  <Box
                    display="flex"
                    alignItems="start"
                    gap={0.5}
                    sx={{ color: 'error.main', width: '100%' }}>
                    <ErrorOutlineIcon fontSize="inherit" sx={{ mt: 0.3 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, wordBreak: 'break-word', display: 'block' }}>
                      Errore: {u.error || 'Impossibile completare il caricamento'}
                    </Typography>
                  </Box>
                )}
              </MenuItem>
              {idx < uploads.length - 1 && <Divider sx={{ mx: 2 }} />}
            </Box>
          );
        })}
      </Menu>
    </Box>
  );
};
export default UploadStatusDropdown;
